import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { guideSpecialties, guideSpecialtyLabels, type GuideSpecialtyType } from '@/types/types_guide';

interface GuideSpecialtiesFieldProps {
  value: GuideSpecialtyType[];
  onChange: (specialties: GuideSpecialtyType[]) => void;
}

/**
 * The checkbox grid for a guide's specialties.
 *
 * A fixed set rather than free text, unlike languages: these are what the public
 * filter bar is built from, so a typo would produce a tag nobody can filter on.
 *
 * Order comes from `guideSpecialties`, not from the order they were ticked, so
 * two guides with the same expertise always list it the same way — and so an
 * autosaved change is a genuine change rather than a reshuffle.
 */
export function GuideSpecialtiesField({ value, onChange }: GuideSpecialtiesFieldProps) {
  function toggle(specialty: GuideSpecialtyType, checked: boolean) {
    const next = checked ? [...value, specialty] : value.filter((s) => s !== specialty);
    onChange(guideSpecialties.filter((s) => next.includes(s)));
  }

  return (
    <div className='tw:grid tw:gap-2.5 tw:sm:grid-cols-3'>
      {guideSpecialties.map((specialty) => (
        <Label
          key={specialty}
          className='tw:flex tw:cursor-pointer tw:items-center tw:gap-2.5 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:font-normal'
        >
          <Checkbox
            checked={value.includes(specialty)}
            onCheckedChange={(checked) => toggle(specialty, checked === true)}
          />
          {guideSpecialtyLabels[specialty]}
        </Label>
      ))}
    </div>
  );
}
